import { RefObject, useEffect, useState } from "react"

/** 드래그 이벤트를 받는 범위(채팅 + 숏폼·AI 요약 등 사이드 전체) */
const CHAT_CONTAINER_SELECTOR = ".contB"
/** 실제로 이미지를 받는 메시지 영역 */
const MESSAGES_SELECTOR = ".chatB"

const findMessagesArea = (target: EventTarget | null) =>
  target instanceof Element ? target.closest(MESSAGES_SELECTOR) : null

// 채팅 메시지를 드래그하는 등 파일이 아닌 경우는 무시한다
const isFileDrag = (e: DragEvent) =>
  Array.from(e.dataTransfer?.types || []).includes("Files")

interface Props {
  /** 드래그 범위(.contB)를 찾기 위한 기준 엘리먼트 */
  anchorRef: RefObject<HTMLElement>
  /** 참이면 드래그&드롭을 받지 않는다 */
  disabled?: boolean
  onDropFile: (file: File) => void
}

/**
 * 메시지 영역에 이미지를 드래그&드롭하면 파일을 넘겨준다.
 * 드래그 중인 동안에는 안내를 띄울 메시지 영역 엘리먼트를 반환한다.
 */
export default function useChatFileDrop({
  anchorRef,
  disabled = false,
  onDropFile,
}: Props) {
  /** 파일을 끌고 있는 동안 안내를 띄울 메시지 영역 */
  const [dropAreaEl, setDropAreaEl] = useState<Element | null>(null)

  useEffect(() => {
    if (disabled) return

    const container = anchorRef.current?.closest(CHAT_CONTAINER_SELECTOR)

    if (!container) return

    const handleDragOver = (e: Event) => {
      const event = e as DragEvent

      if (!isFileDrag(event)) return

      // 막지 않으면 브라우저가 이미지를 새 탭으로 열어버린다.
      // 메시지 영역 밖(숏폼·AI 요약 등)에 떨어뜨렸을 때도 아무 일도 없게 하려고
      // 컨테이너 전체에서 기본 동작을 막고, 실제 처리만 메시지 영역으로 제한한다
      event.preventDefault()
      setDropAreaEl(findMessagesArea(event.target))
    }

    const handleDragLeave = (e: Event) => {
      const next = (e as DragEvent).relatedTarget

      // 컨테이너 내부 요소 간 이동은 벗어난 것이 아니다
      if (next instanceof Node && container.contains(next)) return

      setDropAreaEl(null)
    }

    const handleDrop = (e: Event) => {
      const event = e as DragEvent

      if (!isFileDrag(event)) return

      event.preventDefault()
      setDropAreaEl(null)

      if (!findMessagesArea(event.target)) return

      const dropped = event.dataTransfer?.files?.[0]

      if (!dropped) return

      onDropFile(dropped)
    }

    container.addEventListener("dragover", handleDragOver)
    container.addEventListener("dragleave", handleDragLeave)
    container.addEventListener("drop", handleDrop)

    return () => {
      container.removeEventListener("dragover", handleDragOver)
      container.removeEventListener("dragleave", handleDragLeave)
      container.removeEventListener("drop", handleDrop)
    }
  }, [anchorRef, disabled, onDropFile])

  return dropAreaEl
}
